import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { databaseService } from '../api/database';

const RespaldoPage = () => {
  const { currentUser: user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Protección de Ruta - Solo Super Administradores
  if (!user || !user.is_superuser) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const handleGenerarRespaldo = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Llamar al endpoint para generar el respaldo
      const response = await databaseService.generateBackup();

      // Obtener el nombre del archivo de las cabeceras de respuesta
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'backup.sql';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length === 2) {
          filename = filenameMatch[1];
        }
      }

      // Crear un objeto URL para el archivo descargado
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Crear un enlace temporal y hacer clic en él para iniciar la descarga
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Limpiar después de la descarga
      window.URL.revokeObjectURL(url);
      link.remove();
      
      setSuccess(true);
    } catch (err) {
      console.error('Error al generar respaldo:', err);
      setError('Error al generar el respaldo de la base de datos. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-[#0D2B28] mb-6">Respaldo de Base de Datos</h1>
      
      <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 max-w-2xl">
        <h2 className="text-lg font-semibold mb-4">Generar Copia de Seguridad</h2>
        
        <p className="text-gray-600 mb-6">
          Desde esta sección puede generar una copia de seguridad completa de los datos del sistema.
          El archivo generado es crítico y debe almacenarse en un lugar seguro.
        </p>
        
        <div className="bg-blue-50 border-l-4 border-[#2a9d8f] p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-[#2a9d8f]" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-[#0D2B28]">
                Este proceso es exclusivo para el Administrador del Sistema.
                La descarga puede tardar unos segundos.
              </p>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-800">
                  ¡Copia de seguridad creada correctamente! 
                  El archivo se ha descargado a su computadora.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <button
          onClick={handleGenerarRespaldo}
          disabled={loading}
          className={`w-full flex justify-center items-center gap-2 px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white 
            ${loading ? 'bg-[#2a9d8f]/60 cursor-not-allowed' : 'bg-[#2a9d8f] hover:bg-[#23877a]'} 
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2a9d8f] transition-colors`}
          aria-label="Generar respaldo de base de datos"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generando copia de seguridad...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Generar y Descargar Copia de Seguridad
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default RespaldoPage;